// Shared client-side validators. Server mirrors these rules in the edge functions.

// RFC-lite email regex: requires a real-looking TLD and no whitespace / consecutive dots.
const EMAIL_RE = /^(?!.*\.\.)[A-Za-z0-9._%+\-]+@[A-Za-z0-9](?:[A-Za-z0-9\-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9\-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,24}$/;

export function isValidEmail(raw: string): boolean {
  const s = raw.trim();
  if (!s || s.length > 254) return false;
  if (s.includes("..")) return false;
  const [local, domain] = s.split("@");
  if (!local || !domain || local.length > 64) return false;
  return EMAIL_RE.test(s);
}

/**
 * NANP (US/Canada) phone validation.
 * Accepts common human formats; rejects obvious fakes like 5555555555, 0000000000,
 * 1234567890, and numbers with invalid area/exchange codes.
 */
export function isValidUsPhone(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  // Strip everything but digits and a leading +
  const digits = s.replace(/[^\d]/g, "");
  let ten = digits;
  if (digits.length === 11 && digits.startsWith("1")) ten = digits.slice(1);
  if (ten.length !== 10) return false;

  const area = ten.slice(0, 3);
  const exch = ten.slice(3, 6);
  const line = ten.slice(6);

  // NANP: area & exchange must start with 2-9; second digit not 9 for area.
  if (!/^[2-9][0-8]\d$/.test(area)) return false;
  if (!/^[2-9]\d{2}$/.test(exch)) return false;
  if (!/^\d{4}$/.test(line)) return false;

  // Reject all-identical digits (5555555555, 8888888888, etc.)
  if (/^(\d)\1{9}$/.test(ten)) return false;

  // Reject 555-01XX range (reserved for fictional use)
  if (exch === "555" && /^01\d{2}$/.test(line)) return false;

  // Reject strictly ascending / descending sequences
  const asc = "0123456789";
  const desc = "9876543210";
  if (asc.includes(ten) || desc.includes(ten)) return false;

  return true;
}

export function isValidUsZip(raw: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(raw.trim());
}

/**
 * Progressively formats a US phone number as the user types.
 * Examples: "6" -> "+1 (6", "603424" -> "+1 (603) 424", "6034244608" -> "+1 (603) 424-4608".
 * Accepts existing formatting and a leading country code.
 */
export function formatUsPhone(raw: string): string {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (digits.length < 4) return `+1 (${a}`;
  if (digits.length < 7) return `+1 (${a}) ${b}`;
  return `+1 (${a}) ${b}-${c}`;
}
